/**
 * Mirror of backend `models/src/bi/query.ts`.
 *
 * The structured query the AI and the editor produce. It never carries SQL
 * or scope: the server compiles it against a dataset's catalog and adds the
 * caller's scope itself.
 */

import type { BiFormatSpec } from './format';

export enum BiDatasetRefKind {
  /** A semantic model defined in code over our own database. */
  Sql = 'sql',
  /** A dataset materialized from an uploaded file. */
  Upload = 'upload',
}

export type BiDatasetRef =
  | { kind: BiDatasetRefKind.Sql; model: string }
  | { kind: BiDatasetRefKind.Upload; datasetId: string };

export enum BiAggregation {
  Sum = 'sum',
  Avg = 'avg',
  Min = 'min',
  Max = 'max',
  Count = 'count',
  CountDistinct = 'count_distinct',
}

export enum BiTimeGrain {
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year',
}

export enum BiMeasureKind {
  Field = 'field',
  /** One measure divided by another: a rate or an average per unit. */
  Ratio = 'ratio',
}

export interface BiFieldMeasureRef {
  kind: BiMeasureKind.Field;
  field: string;
  /** Omitted: the field's default aggregation. */
  agg?: BiAggregation;
}

export interface BiRatioMeasureRef {
  kind: BiMeasureKind.Ratio;
  /** The result column key; must not collide with any other key. */
  key: string;
  numerator: BiFieldMeasureRef;
  denominator: BiFieldMeasureRef;
  /** The column's display name; omitted: "numerator / denominator". */
  label?: string;
  /** Omitted: a plain number. A rate is `percent` (a fraction, 0.5 = 50%). */
  format?: BiFormatSpec;
}

export type BiMeasureRef = BiFieldMeasureRef | BiRatioMeasureRef;

export interface BiDimensionRef {
  field: string;
  /** Only on time fields. */
  grain?: BiTimeGrain;
  /**
   * Bands or relabels the field: each row reads the label of the first
   * branch it meets. Not with `grain`; needs `key`.
   */
  case?: BiDimensionCase;
  /**
   * The result column key of a cased dimension (`mileage_band`), so two
   * bandings of one field are two columns. Refused without `case`.
   */
  key?: string;
  /** The column's display name; omitted: the field's, marked as banded. */
  label?: string;
}

/** A condition on the cased dimension's own field: a filter's op alone. */
export type BiCaseCondition =
  | Omit<BiComparisonFilter, 'field'>
  | Omit<BiListFilter, 'field'>
  | Omit<BiBetweenFilter, 'field'>
  | Omit<BiContainsFilter, 'field'>
  | Omit<BiNullFilter, 'field'>;

export interface BiCaseBranch {
  when: BiCaseCondition;
  label: string;
}

export interface BiDimensionCase {
  /** Evaluated in order; rows sort in this order too, `else` last. */
  branches: BiCaseBranch[];
  /** The label of every other row; omitted: empty. */
  else?: string;
}

export enum BiFilterOp {
  Eq = 'eq',
  Neq = 'neq',
  Gt = 'gt',
  Gte = 'gte',
  Lt = 'lt',
  Lte = 'lte',
  In = 'in',
  NotIn = 'not_in',
  Between = 'between',
  Contains = 'contains',
  IsNull = 'is_null',
  NotNull = 'not_null',
}

export type BiScalar = string | number | boolean;

export interface BiComparisonFilter {
  field: string;
  op:
    | BiFilterOp.Eq
    | BiFilterOp.Neq
    | BiFilterOp.Gt
    | BiFilterOp.Gte
    | BiFilterOp.Lt
    | BiFilterOp.Lte;
  value: BiScalar;
}

export interface BiListFilter {
  field: string;
  op: BiFilterOp.In | BiFilterOp.NotIn;
  values: (string | number)[];
}

export interface BiBetweenFilter {
  field: string;
  op: BiFilterOp.Between;
  from: string | number;
  to: string | number;
}

export interface BiContainsFilter {
  field: string;
  op: BiFilterOp.Contains;
  value: string;
}

export interface BiNullFilter {
  field: string;
  op: BiFilterOp.IsNull | BiFilterOp.NotNull;
}

export type BiFilter =
  | BiComparisonFilter
  | BiListFilter
  | BiBetweenFilter
  | BiContainsFilter
  | BiNullFilter;

/** Resolved in store local time when the query runs, so cards stay live. */
export enum BiRelativeTimePreset {
  Today = 'today',
  Yesterday = 'yesterday',
  Last7Days = 'last_7_days',
  Last30Days = 'last_30_days',
  Last90Days = 'last_90_days',
  Last12Months = 'last_12_months',
  ThisWeek = 'this_week',
  LastWeek = 'last_week',
  ThisMonth = 'this_month',
  LastMonth = 'last_month',
  ThisQuarter = 'this_quarter',
  LastQuarter = 'last_quarter',
  ThisYear = 'this_year',
  LastYear = 'last_year',
}

export enum BiTimeRangeKind {
  Relative = 'relative',
  /** The last N calendar units, re-resolved on every run. */
  Rolling = 'rolling',
  Absolute = 'absolute',
}

/**
 * "Recent N units": calendar-aligned for week (ISO, from Monday), month,
 * quarter and year. With `includeCurrent` it runs through today, the
 * current unit counting as one of the N ("近 3 个月" in October is
 * Aug 1 – Oct 17); without, it is the N whole units before the current one.
 */
export interface BiRollingTimeRange {
  kind: BiTimeRangeKind.Rolling;
  unit: BiTimeGrain;
  count: number;
  includeCurrent: boolean;
}

/**
 * The most units a rolling range may count, per unit: 366, and never more
 * than the ten years an absolute range may span.
 */
export const kBiMaxRollingCount: Record<BiTimeGrain, number> = {
  [BiTimeGrain.Day]: 366,
  [BiTimeGrain.Week]: 366,
  [BiTimeGrain.Month]: 120,
  [BiTimeGrain.Quarter]: 40,
  [BiTimeGrain.Year]: 10,
};

export type BiTimeRange =
  | { kind: BiTimeRangeKind.Relative; preset: BiRelativeTimePreset }
  | BiRollingTimeRange
  /** Inclusive store-local dates, `YYYY-MM-DD`. */
  | { kind: BiTimeRangeKind.Absolute; from: string; to: string };

/** Inclusive store-local dates, `YYYY-MM-DD`, a range resolved to. */
export interface BiDateWindow {
  from: string;
  to: string;
}

const kRollingText = /^rolling:([a-z]+):(\d+):(current|whole)$/;

/** A `rolling:…` token back as a range, unchecked; null for anything else. */
export function biRollingTimeRangeOfText(
  text: string,
): BiRollingTimeRange | null {
  const match = kRollingText.exec(text);
  const unit = Object.values(BiTimeGrain).find(grain => grain === match?.[1]);
  if (!match || !unit) return null;
  return {
    kind: BiTimeRangeKind.Rolling,
    unit,
    count: Number(match[2]),
    includeCurrent: match[3] === 'current',
  };
}

export enum BiSortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

export interface BiSort {
  /** A result column key: a dimension or measure key. */
  key: string;
  dir: BiSortDirection;
}

/**
 * A condition on a measure's value per group, SQL `having`: "orders per
 * plate ≥ 2". `measure` is a measure's result key in the same query.
 */
export interface BiMeasureFilter {
  measure: string;
  op:
    | BiFilterOp.Eq
    | BiFilterOp.Gt
    | BiFilterOp.Gte
    | BiFilterOp.Lt
    | BiFilterOp.Lte;
  value: number;
}

export interface BiQuerySpec {
  dataset: BiDatasetRef;
  measures: BiMeasureRef[];
  dimensions: BiDimensionRef[];
  filters: BiFilter[];
  /** Applies to the dataset's primary time field. */
  timeRange?: BiTimeRange;
  sort: BiSort[];
  limit?: number;
  /**
   * Drops the groups whose every measure is zero or empty (a ratio counts
   * by its numerator and denominator): the items a ranking would otherwise
   * list with nothing to rank. Needs a dimension.
   */
  excludeEmpty?: boolean;
  /** Keeps only the groups whose measures meet every condition. */
  having?: BiMeasureFilter[];
}

/** The result column key of a measure. */
export function biMeasureKey(ref: BiMeasureRef): string {
  if (ref.kind === BiMeasureKind.Ratio) return ref.key;
  return ref.agg ? `${ref.field}__${ref.agg}` : ref.field;
}

/** The result column key of a dimension. */
export function biDimensionKey(ref: BiDimensionRef): string {
  if (ref.key !== undefined) return ref.key;
  return ref.grain ? `${ref.field}__${ref.grain}` : ref.field;
}

/** A cased dimension's labels in sort order: its branches', then `else`. */
export function biCaseLabels(cased: BiDimensionCase): string[] {
  const labels = cased.branches.map(branch => branch.label);
  return cased.else === undefined ? labels : [...labels, cased.else];
}
